import { useState, useEffect, useMemo } from 'react'
import {
  BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts'
import { api } from '../services/api'
import { useAuth } from '../hooks/useAuth'

interface License {
  id: string
  customerId?: string
  customerName?: string
  productName: string
  skuPartNumber?: string
  totalUnits: number
  activeUnits?: number
  consumedUnits?: number
  availableUnits?: number
  capabilityStatus?: string
}

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16']

export default function Usage() {
  const { getAccessToken } = useAuth()
  const [licenses, setLicenses] = useState<License[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Filter states
  const [selectedCustomer, setSelectedCustomer] = useState<string>('all')
  const [selectedProduct, setSelectedProduct] = useState<string>('all')
  const [showLowUtilizationOnly, setShowLowUtilizationOnly] = useState(false)
  const [sortField, setSortField] = useState<string>('utilizationRate')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')

  useEffect(() => {
    async function fetchLicenses() {
      try {
        setLoading(true)
        setError(null)
        const token = await getAccessToken()
        const data = await api.getLicenses(token)
        setLicenses(data.licenses || [])
      } catch (err: any) {
        console.error('Failed to fetch licenses:', err)
        setError(err.message || 'Failed to load license data')
      } finally {
        setLoading(false)
      }
    }

    fetchLicenses()
  }, [getAccessToken])

  // Build unique customers list
  const customers = useMemo(() => {
    const customerMap = new Map<string, string>()
    licenses.forEach(l => {
      if (l.customerId && l.customerName) {
        customerMap.set(l.customerId, l.customerName)
      }
    })
    return Array.from(customerMap.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [licenses])

  // Build unique products list
  const products = useMemo(() => {
    const productSet = new Set<string>()
    licenses.forEach(l => productSet.add(l.productName))
    return Array.from(productSet).sort()
  }, [licenses])

  // Calculate utilization for each license
  const licensesWithUtilization = useMemo(() => {
    return licenses.map(l => {
      const total = l.totalUnits || 0
      const consumed = l.consumedUnits || 0
      const available = l.availableUnits || 0
      const utilizationRate = total > 0 ? (consumed / total) * 100 : 0
      return {
        ...l,
        consumed,
        available,
        utilizationRate,
      }
    })
  }, [licenses])

  // Apply filters
  const filteredLicenses = useMemo(() => {
    return licensesWithUtilization.filter(l => {
      if (selectedCustomer !== 'all' && l.customerId !== selectedCustomer) return false
      if (selectedProduct !== 'all' && l.productName !== selectedProduct) return false
      if (showLowUtilizationOnly && l.utilizationRate >= 50) return false
      return true
    })
  }, [licensesWithUtilization, selectedCustomer, selectedProduct, showLowUtilizationOnly])

  // Calculate summary stats
  const stats = useMemo(() => {
    const totalLicenses = filteredLicenses.reduce((sum, l) => sum + (l.totalUnits || 0), 0)
    const totalConsumed = filteredLicenses.reduce((sum, l) => sum + (l.consumedUnits || 0), 0)
    const totalAvailable = filteredLicenses.reduce((sum, l) => sum + (l.availableUnits || 0), 0)
    const utilizationRate = totalLicenses > 0 ? ((totalConsumed / totalLicenses) * 100).toFixed(1) : '0.0'
    const lowUtilizationCount = licensesWithUtilization.filter(l => l.utilizationRate < 50 && l.totalUnits > 0).length

    return {
      totalLicenses,
      totalConsumed,
      totalAvailable,
      utilizationRate,
      lowUtilizationCount,
      uniqueProducts: new Set(filteredLicenses.map(l => l.productName)).size,
    }
  }, [filteredLicenses, licensesWithUtilization])

  // Chart data: Utilization by Product (aggregated)
  const utilizationByProduct = useMemo(() => {
    const productMap = new Map<string, { consumed: number; available: number; total: number }>()

    filteredLicenses.forEach(l => {
      const existing = productMap.get(l.productName) || { consumed: 0, available: 0, total: 0 }
      productMap.set(l.productName, {
        consumed: existing.consumed + (l.consumedUnits || 0),
        available: existing.available + (l.availableUnits || 0),
        total: existing.total + (l.totalUnits || 0)
      })
    })

    return Array.from(productMap.entries())
      .map(([name, data]) => ({
        name: name.length > 30 ? name.substring(0, 30) + '...' : name,
        fullName: name,
        consumed: data.consumed,
        available: data.available,
        total: data.total,
        utilizationRate: data.total > 0 ? ((data.consumed / data.total) * 100).toFixed(1) : '0.0'
      }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 10)
  }, [filteredLicenses])

  // Chart data: Utilization by Customer
  const utilizationByCustomer = useMemo(() => {
    const customerMap = new Map<string, { name: string; consumed: number; total: number }>()

    filteredLicenses.forEach(l => {
      if (!l.customerId) return
      const existing = customerMap.get(l.customerId) || { name: l.customerName || 'Unknown', consumed: 0, total: 0 }
      customerMap.set(l.customerId, {
        name: existing.name,
        consumed: existing.consumed + (l.consumedUnits || 0),
        total: existing.total + (l.totalUnits || 0)
      })
    })

    return Array.from(customerMap.entries())
      .map(([id, data]) => ({
        customerId: id,
        name: data.name.length > 25 ? data.name.substring(0, 25) + '...' : data.name,
        fullName: data.name,
        consumed: data.consumed,
        total: data.total,
        utilizationRate: data.total > 0 ? ((data.consumed / data.total) * 100).toFixed(1) : '0.0'
      }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 10)
  }, [filteredLicenses])

  // Chart data: Overall utilization pie chart
  const overallUtilization = useMemo(() => {
    const totalConsumed = filteredLicenses.reduce((sum, l) => sum + (l.consumedUnits || 0), 0)
    const totalAvailable = filteredLicenses.reduce((sum, l) => sum + (l.availableUnits || 0), 0)
    return [
      { name: 'Consumed', value: totalConsumed, color: '#10b981' },
      { name: 'Available', value: totalAvailable, color: '#f59e0b' }
    ]
  }, [filteredLicenses])

  // Low utilization products alert list
  const lowUtilizationProducts = useMemo(() => {
    const productMap = new Map<string, { consumed: number; total: number; customers: Set<string> }>()

    licensesWithUtilization
      .filter(l => l.totalUnits > 0)
      .forEach(l => {
        const existing = productMap.get(l.productName) || { consumed: 0, total: 0, customers: new Set<string>() }
        existing.consumed += l.consumedUnits || 0
        existing.total += l.totalUnits || 0
        if (l.customerName) existing.customers.add(l.customerName)
        productMap.set(l.productName, existing)
      })

    return Array.from(productMap.entries())
      .map(([name, data]) => ({
        productName: name,
        consumed: data.consumed,
        total: data.total,
        utilizationRate: data.total > 0 ? (data.consumed / data.total) * 100 : 0,
        customerCount: data.customers.size,
        wastedLicenses: data.total - data.consumed
      }))
      .filter(p => p.utilizationRate < 50)
      .sort((a, b) => a.utilizationRate - b.utilizationRate)
      .slice(0, 8)
  }, [licensesWithUtilization])

  // Sorted data for table
  const sortedLicenses = useMemo(() => {
    return [...filteredLicenses].sort((a, b) => {
      let aVal: any = a[sortField as keyof typeof a]
      let bVal: any = b[sortField as keyof typeof b]

      if (sortField === 'utilizationRate') {
        aVal = a.utilizationRate
        bVal = b.utilizationRate
      }

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal)
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1
      return 0
    })
  }, [filteredLicenses, sortField, sortDirection])

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const getUtilizationStatus = (rate: number) => {
    if (rate >= 70) return { label: 'Good', color: 'text-green-600', bg: 'bg-green-100' }
    if (rate >= 50) return { label: 'Moderate', color: 'text-yellow-600', bg: 'bg-yellow-100' }
    return { label: 'Low', color: 'text-red-600', bg: 'bg-red-100' }
  }

  const exportToCSV = () => {
    const headers = ['Product', 'Customer', 'Total Licenses', 'Consumed', 'Available', 'Utilization %', 'Status']
    const rows = sortedLicenses.map(l => {
      const status = getUtilizationStatus(l.utilizationRate)
      return [
        l.productName,
        l.customerName || 'N/A',
        l.totalUnits,
        l.consumed,
        l.available,
        l.utilizationRate.toFixed(1),
        status.label,
      ]
    })

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `m365-license-utilization-${new Date().toISOString().split('T')[0]}.csv`
    a.click()
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading license data...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">Error: {error}</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-2 text-red-600 hover:text-red-800 underline"
        >
          Retry
        </button>
      </div>
    )
  }

  return (
    <div className="px-4 sm:px-0">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Microsoft 365 License Utilization</h2>
        <p className="mt-2 text-sm text-gray-700">
          Monitor license consumption and identify optimization opportunities.
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Customer</label>
            <select
              value={selectedCustomer}
              onChange={(e) => setSelectedCustomer(e.target.value)}
              className="w-full rounded-md border border-gray-300 bg-white text-gray-900 shadow-sm focus:border-primary-500 focus:ring-primary-500 py-2.5 px-3 text-base"
            >
              <option value="all">All Customers</option>
              {customers.map(customer => (
                <option key={customer.id} value={customer.id}>
                  {customer.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Product</label>
            <select
              value={selectedProduct}
              onChange={(e) => setSelectedProduct(e.target.value)}
              className="w-full rounded-md border border-gray-300 bg-white text-gray-900 shadow-sm focus:border-primary-500 focus:ring-primary-500 py-2.5 px-3 text-base"
            >
              <option value="all">All Products</option>
              {products.map(product => (
                <option key={product} value={product}>
                  {product}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={showLowUtilizationOnly}
                onChange={(e) => setShowLowUtilizationOnly(e.target.checked)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Show Low Utilization Only (&lt;50%)</span>
            </label>
          </div>
          <div className="flex items-end">
            <button
              onClick={exportToCSV}
              disabled={filteredLicenses.length === 0}
              className="w-full bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              Export to CSV
            </button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-6">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <dt className="text-sm font-medium text-gray-500 truncate">Total Licenses</dt>
            <dd className="mt-1 text-3xl font-semibold text-primary-600">
              {stats.totalLicenses.toLocaleString()}
            </dd>
            <p className="mt-1 text-xs text-gray-500">{stats.uniqueProducts} products</p>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <dt className="text-sm font-medium text-gray-500 truncate">Consumed</dt>
            <dd className="mt-1 text-3xl font-semibold text-green-600">
              {stats.totalConsumed.toLocaleString()}
            </dd>
            <p className="mt-1 text-xs text-gray-500">Licenses in use</p>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <dt className="text-sm font-medium text-gray-500 truncate">Available</dt>
            <dd className="mt-1 text-3xl font-semibold text-orange-500">
              {stats.totalAvailable.toLocaleString()}
            </dd>
            <p className="mt-1 text-xs text-gray-500">Licenses unused</p>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <dt className="text-sm font-medium text-gray-500 truncate">Utilization Rate</dt>
            <dd className="mt-1 text-3xl font-semibold text-purple-600">
              {stats.utilizationRate}%
            </dd>
            <p className="mt-1 text-xs text-gray-500">
              {stats.lowUtilizationCount > 0 && (
                <span className="text-red-500">{stats.lowUtilizationCount} products &lt;50%</span>
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Utilization by Product */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Utilization by Product</h3>
          {utilizationByProduct.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={utilizationByProduct} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="name" type="category" width={180} tick={{ fontSize: 11 }} />
                <Tooltip
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      const data = payload[0].payload
                      return (
                        <div className="bg-white p-3 border rounded shadow-lg">
                          <p className="font-medium text-sm">{data.fullName}</p>
                          <p className="text-sm text-green-600">Consumed: {data.consumed}</p>
                          <p className="text-sm text-orange-500">Available: {data.available}</p>
                          <p className="text-sm text-gray-600">Total: {data.total}</p>
                          <p className="text-sm font-medium">Utilization: {data.utilizationRate}%</p>
                        </div>
                      )
                    }
                    return null
                  }}
                />
                <Legend />
                <Bar dataKey="consumed" stackId="a" fill="#10b981" name="Consumed" />
                <Bar dataKey="available" stackId="a" fill="#f59e0b" name="Available" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-500">No data available</div>
          )}
        </div>

        {/* Overall Utilization Pie */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Overall License Distribution</h3>
          {overallUtilization[0].value > 0 || overallUtilization[1].value > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={overallUtilization}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  innerRadius={60}
                  label={(entry) => `${entry.name}: ${(entry.value || 0).toLocaleString()}`}
                >
                  {overallUtilization.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-500">No data available</div>
          )}
        </div>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Top Customers */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Top Customers by License Count</h3>
          {utilizationByCustomer.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={utilizationByCustomer} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="name" type="category" width={150} tick={{ fontSize: 11 }} />
                <Tooltip
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      const data = payload[0].payload
                      return (
                        <div className="bg-white p-3 border rounded shadow-lg">
                          <p className="font-medium text-sm">{data.fullName}</p>
                          <p className="text-sm text-blue-600">Total: {data.total}</p>
                          <p className="text-sm text-green-600">Consumed: {data.consumed}</p>
                          <p className="text-sm font-medium">Utilization: {data.utilizationRate}%</p>
                        </div>
                      )
                    }
                    return null
                  }}
                />
                <Bar dataKey="total" fill="#3b82f6" name="Total Licenses" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-500">No data available</div>
          )}
        </div>

        {/* Low Utilization Alert */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            <span className="text-red-500">Low Utilization Alerts</span>
            <span className="text-sm font-normal text-gray-500 ml-2">(&lt;50% utilization)</span>
          </h3>
          {lowUtilizationProducts.length > 0 ? (
            <div className="space-y-3 max-h-72 overflow-y-auto">
              {lowUtilizationProducts.map((product, index) => (
                <div key={product.productName} className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-100">
                  <div className="flex items-center space-x-3 flex-1 min-w-0">
                    <div
                      className="w-3 h-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: COLORS[index % COLORS.length] }}
                    />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate" title={product.productName}>
                        {product.productName.length > 35 ? product.productName.substring(0, 35) + '...' : product.productName}
                      </p>
                      <p className="text-xs text-gray-500">
                        {product.customerCount} customer{product.customerCount !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0 ml-4">
                    <div className="text-sm font-semibold text-red-600">
                      {product.utilizationRate.toFixed(1)}%
                    </div>
                    <div className="text-xs text-gray-500">
                      {product.wastedLicenses} unused
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center h-64 text-green-600">
              <div className="text-center">
                <svg className="mx-auto h-12 w-12 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="mt-2">All products have good utilization!</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Detailed Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">License Details</h3>
          <p className="mt-1 text-sm text-gray-500">
            Showing {filteredLicenses.length} license records
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  onClick={() => handleSort('productName')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                >
                  Product {sortField === 'productName' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th
                  onClick={() => handleSort('customerName')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                >
                  Customer {sortField === 'customerName' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th
                  onClick={() => handleSort('totalUnits')}
                  className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                >
                  Total {sortField === 'totalUnits' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Consumed
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Available
                </th>
                <th
                  onClick={() => handleSort('utilizationRate')}
                  className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                >
                  Utilization {sortField === 'utilizationRate' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedLicenses.slice(0, 50).map((license) => {
                const status = getUtilizationStatus(license.utilizationRate)
                return (
                  <tr key={`${license.id}-${license.customerId}`} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <span title={license.productName}>
                        {license.productName.length > 40 ? license.productName.substring(0, 40) + '...' : license.productName}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {license.customerName || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                      {(license.totalUnits || 0).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 text-right">
                      {(license.consumed || 0).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-orange-500 text-right">
                      {(license.available || 0).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-right">
                      {license.utilizationRate.toFixed(1)}%
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${status.bg} ${status.color}`}>
                        {status.label}
                      </span>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        {filteredLicenses.length > 50 && (
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
            <p className="text-sm text-gray-500">
              Showing first 50 of {filteredLicenses.length} records. Use filters to narrow results or export to CSV for full data.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
